import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [actionFilter, setActionFilter] = useState("");

  const fetchLogs = useCallback(async () => {
    const params = { page, per_page: 50 };
    if (actionFilter) params.action = actionFilter;
    const res = await api.get("/admin/audit-log", { params });
    setLogs(res.data.logs);
    setTotal(res.data.total);
  }, [page, actionFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Audit Log</h1>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-48 rounded-none h-9 text-sm" data-testid="audit-action-filter">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="LOGIN">Login</SelectItem>
            <SelectItem value="CHARGE">Charge</SelectItem>
            <SelectItem value="VOID">Void</SelectItem>
            <SelectItem value="CREATE">Create</SelectItem>
            <SelectItem value="UPDATE">Update</SelectItem>
            <SelectItem value="UPDATE_SETTING">Settings</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full table-dense">
          <thead><tr className="border-b border-gray-100">
            <th className="w-8"></th><th className="text-left">Time</th><th className="text-left">User</th>
            <th className="text-left">Action</th><th className="text-left">Entity</th>
            <th className="text-left">Device ID</th><th className="text-left">IP</th>
          </tr></thead>
          <tbody>
            {logs.map((log) => (
              <>
                <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                  <td>{expanded === log.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</td>
                  <td className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="font-medium text-sm">{log.display_name}</td>
                  <td><span className="text-[10px] font-bold uppercase bg-gray-100 px-2 py-0.5">{log.action}</span></td>
                  <td className="text-sm text-gray-600">{log.entity_type}{log.entity_id ? ` #${log.entity_id.slice(0, 8)}` : ""}</td>
                  <td className="font-mono text-xs text-gray-400">{log.device_cookie_id?.slice(0, 8) || "—"}</td>
                  <td className="font-mono text-xs text-gray-400">{log.ip_address}</td>
                </tr>
                {expanded === log.id && (
                  <tr key={`${log.id}-detail`} className="bg-gray-50">
                    <td colSpan={7} className="p-4">
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-gray-400 uppercase tracking-wider block mb-1">Device Summary</span>
                          {log.device_summary || "—"}
                        </div>
                        <div>
                          <span className="text-gray-400 uppercase tracking-wider block mb-1">Record Hash</span>
                          <code className="text-[10px] break-all">{log.record_hash}</code>
                        </div>
                        {log.old_value && (
                          <div>
                            <span className="text-gray-400 uppercase tracking-wider block mb-1">Before</span>
                            <pre className="text-[10px] bg-white p-2 border border-gray-200 overflow-auto max-h-32">{JSON.stringify(log.old_value, null, 2)}</pre>
                          </div>
                        )}
                        {log.new_value && (
                          <div>
                            <span className="text-gray-400 uppercase tracking-wider block mb-1">After</span>
                            <pre className="text-[10px] bg-white p-2 border border-gray-200 overflow-auto max-h-32">{JSON.stringify(log.new_value, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {logs.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-gray-400">No audit records</td></tr>}
          </tbody>
        </table>
      </div>

      {total > 50 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">{total} total records</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-none h-7 text-xs">Prev</Button>
            <Button variant="outline" size="sm" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} className="rounded-none h-7 text-xs">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
